import React, { useState, useEffect, useRef } from 'react';
import { Loader } from "components/Loader";
import { getServicesManager } from "services";
import { RecordedGreeting } from "interfaces/db";
import { clearCache, SERVICE_CALLS } from 'extension-helpers';
import { AudioRecorder } from 'lib/recorder';
import { Typography } from 'interfaces/typography';
import { PrimaryActionButton, StandardContainer } from 'utils/CommonUIComponents';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
  alpha
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  VoiceChat as VoiceChatIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';

type WorkflowStep = 'initial' | 'recording' | 'recorded';

export const RecordVM: React.FC = () => {
  const [greetings, setGreetings] = useState<RecordedGreeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>('initial');
  const [newRecordingName, setNewRecordingName] = useState('');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [recordingTime, setRecordingTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);

  const mediaRecorderRef = useRef<AudioRecorder | null>(null);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    clearCache(SERVICE_CALLS.PARALLEL_SETTINGS)
    clearCache(SERVICE_CALLS.PARALLEL_VM_OPTIONS)
  }, []);

  // State for editing
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedName, setEditedName] = useState<string>('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    getServicesManager().getGreetings().then((fetchedGreetings: RecordedGreeting[] | null) => {
      if (fetchedGreetings) {
        setGreetings(fetchedGreetings);
      }
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (isRecording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }

    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    };
  }, [isRecording]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    setRecordingTime(0);
    setIsRecording(true);
    const recorder = mediaRecorderRef.current = new AudioRecorder()
    recorder.ready.then(() => setWorkflowStep('recording'))
    recorder.blob.then(audioBlob => {
      setAudioBlob(audioBlob);
      setWorkflowStep('recorded');
      setIsRecording(false);
    })
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.ensureStopped();
    setIsRecording(false);
  };

  const handleRecordNewVoicemail = () => {
    if (workflowStep === 'initial') {
      startRecording();
    } else if (workflowStep === 'recording') {
      stopRecording();
    }
  };

  const handleSaveRecording = async () => {
    if (newRecordingName && audioBlob) {
      try {
        const servicesManager = getServicesManager();
        
        // Convert the audioBlob to a File object and upload
        const file = new File([audioBlob], `${newRecordingName}.wav`, { type: audioBlob.type });
        const result = await servicesManager.uploadGreeting(newRecordingName, file);
        
        if (result) {
          // If upload is successful, update the local state
          setGreetings([...greetings, result]);
          setNewRecordingName('');
          setWorkflowStep('initial');
          setAudioBlob(null);
          setRecordingTime(0);
          setNotification({
            open: true,
            message: 'Voicemail saved successfully!',
            severity: 'success'
          });
        } else {
          setNotification({
            open: true,
            message: 'Failed to save the voicemail. Please try again.',
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error saving voicemail:', error);
        setNotification({
          open: true,
          message: 'An error occurred while saving the voicemail.',
          severity: 'error'
        });
      }
    }
  };

  const handleDiscardRecording = () => {
    setWorkflowStep('initial');
    setAudioBlob(null);
    setNewRecordingName('');
    setRecordingTime(0);
  };

  const handleEdit = async (index: number) => {
    setEditingIndex(index);
    setEditedName(greetings[index].name);
    setEditDialogOpen(true);

    try {
      const url = await getServicesManager().getSpecificGreetingURL(greetings[index].recorded_greeting_id);
      if (url) {
        setAudioURL(url);
      } else {
        console.error('Fetched data is not a url:', JSON.stringify(url));
        setNotification({
          open: true,
          message: 'Failed to load voicemail audio.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching voicemail audio:', error);
      setNotification({
        open: true,
        message: 'Failed to load voicemail audio.',
        severity: 'error'
      });
    }
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null) return;
    
    const oldGreeting = greetings[editingIndex];
    const newName = editedName.trim();

    if (!newName) {
      setNotification({
        open: true,
        message: 'Voicemail name cannot be empty.',
        severity: 'warning'
      });
      return;
    }

    try {
      const servicesManager = getServicesManager();
      const updatedGreeting = await servicesManager.renameGreeting(oldGreeting.recorded_greeting_id, newName);

      if (updatedGreeting) {
        const updatedGreetings = [...greetings];
        updatedGreetings[editingIndex] = updatedGreeting;
        setGreetings(updatedGreetings);
        setEditDialogOpen(false);
        setEditingIndex(null);
        setEditedName('');
        setAudioURL(null);
        setNotification({
          open: true,
          message: 'Voicemail renamed successfully!',
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: 'Failed to rename voicemail. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error renaming voicemail:', error);
      setNotification({
        open: true,
        message: 'An error occurred while renaming the voicemail.',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const success = await getServicesManager().deleteGreeting(greetings[index].recorded_greeting_id);
      if (success) {
        setGreetings([...greetings.slice(0, index), ...greetings.slice(index + 1)])
        setNotification({
          open: true,
          message: 'Voicemail deleted successfully!',
          severity: 'success'
        });
      } else {
        setNotification({
          open: true,
          message: 'Failed to delete voicemail. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting voicemail:', error);
      setNotification({
        open: true,
        message: 'An error occurred while deleting the voicemail.',
        severity: 'error'
      });
    }
  }

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingIndex(null);
    setEditedName('');
    setAudioURL(null);
  };

  if (isLoading) return <Loader />;

  return (
    <StandardContainer maxWidth="max-w-5xl">
      {/* Header */}
      <section className="flex justify-between items-center">
        <div>
          <Typography variant="h3">Record VM</Typography>
          <Typography variant="mediumParagraph" color="#6B7280">
            Create and manage voicemail recordings
          </Typography>
        </div>
      </section>

      {/* Recording Section */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid rgb(229, 231, 235)',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {workflowStep === 'initial' && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <VoiceChatIcon sx={{ fontSize: 40, color: 'rgb(156, 163, 175)', mb: 1.5 }} />
              <div style={{ marginBottom: '4px' }}>
                <Typography variant="h5">Create New Voicemail</Typography>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="mediumParagraph" color="#6B7280">
                  Record a new voicemail greeting that you can reuse
                </Typography>
              </div>
              <PrimaryActionButton
                icon={<MicIcon />}
                onClick={handleRecordNewVoicemail}
                className="px-3 py-1.5 bg-red-500 hover:bg-red-600"
              >
                Start Recording
              </PrimaryActionButton>
            </Box>
          )}
          
          {workflowStep === 'recording' && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 1, 
                mb: 2 
              }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: 'rgb(239, 68, 68)',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.5 }
                  },
                  animation: 'pulse 1s infinite'
                }} />
                <Typography variant="h5" color="#EF4444" fontWeight={500}>
                  Recording...
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: 0.5, 
                mb: 2 
              }}>
                <AccessTimeIcon sx={{ fontSize: 20, color: 'rgb(107, 114, 128)' }} />
                <Typography variant="mediumParagraph" color="#6B7280" style={{ fontFamily: 'monospace' }}>
                  {formatTime(recordingTime)}
                </Typography>
              </Box>
              <PrimaryActionButton
                icon={<StopIcon />}
                onClick={handleRecordNewVoicemail}
                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700"
              >
                Stop Recording
              </PrimaryActionButton>
            </Box>
          )}
          
          {workflowStep === 'recorded' && (
            <Box sx={{ py: 1 }}>
              <div style={{ marginBottom: '12px' }}>
                <Typography variant="h5">Review Recording</Typography>
              </div>
              {audioBlob && (
                <Box sx={{ mb: 2 }}>
                  <audio 
                    src={URL.createObjectURL(audioBlob)} 
                    controls 
                    style={{ width: '100%', marginBottom: '12px' }}
                  />
                </Box>
              )}
              <TextField
                fullWidth
                label="Voicemail Name"
                placeholder="Enter a name for this voicemail"
                value={newRecordingName}
                onChange={(e) => setNewRecordingName(e.target.value)}
                sx={{ mb: 2 }}
                size="medium"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <PrimaryActionButton
                  icon={<SaveIcon />}
                  onClick={handleSaveRecording}
                  disabled={!newRecordingName || !audioBlob}
                  className="flex-1 py-1.5 bg-green-500 hover:bg-green-600 disabled:bg-gray-300"
                >
                  Save Voicemail
                </PrimaryActionButton>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleDiscardRecording}
                  sx={{
                    flex: 1,
                    borderRadius: '8px',
                    py: 1.5,
                    borderColor: 'rgb(229, 231, 235)',
                    color: 'rgb(75, 85, 99)',
                    '&:hover': {
                      borderColor: 'rgb(107, 114, 128)',
                      backgroundColor: alpha('rgb(0, 0, 0)', 0.04)
                    },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Discard
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Saved Voicemails Section */}
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: '1px solid rgb(229, 231, 235)'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <div style={{ marginBottom: '24px' }}>
            <Typography variant="h5">Saved Voicemails</Typography>
          </div>
          
          {greetings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <VoiceChatIcon sx={{ fontSize: 48, color: 'rgb(156, 163, 175)', mb: 2 }} />
              <div style={{ marginBottom: '8px' }}>
                <Typography variant="h5">No saved voicemails yet</Typography>
              </div>
              <Typography variant="mediumParagraph" color="#6B7280">
                Create your first voicemail recording to get started
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {greetings.map((greeting, index) => (
                <Grid item xs={12} sm={6} md={4} key={greeting.recorded_greeting_id}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: '8px',
                      border: '1px solid rgb(229, 231, 235)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: 'rgb(59, 130, 246)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 16px -8px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2, pb: 1 }}>
                      <Typography
                        variant="largeParagraph"
                        fontWeight={500}
                        color="#111827"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {greeting.name}
                      </Typography>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                      <Tooltip title="Edit voicemail">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(index)}
                          sx={{
                            color: 'rgb(107, 114, 128)',
                            '&:hover': {
                              backgroundColor: 'rgba(59, 130, 246, 0.04)',
                              color: 'rgb(59, 130, 246)'
                            }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete voicemail">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(index)}
                          sx={{
                            color: 'rgb(107, 114, 128)',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 68, 68, 0.04)',
                              color: 'rgb(239, 68, 68)'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            border: '1px solid rgb(229, 231, 235)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5">Edit Voicemail</Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {audioURL && (
            <Box sx={{ mb: 2 }}>
              <audio
                src={audioURL}
                controls
                style={{ width: '100%' }}
              />
            </Box>
          )}
          <TextField
            fullWidth
            label="Voicemail Name"
            placeholder="Enter new voicemail name"
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            size="medium"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelEdit}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              borderColor: 'rgb(229, 231, 235)',
              color: 'rgb(75, 85, 99)',
              '&:hover': {
                borderColor: 'rgb(107, 114, 128)',
                backgroundColor: alpha('rgb(0, 0, 0)', 0.04)
              },
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={{
              borderRadius: '8px',
              backgroundColor: 'rgb(34, 197, 94)',
              '&:hover': {
                backgroundColor: 'rgb(22, 163, 74)',
              },
              textTransform: 'none'
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ 
            borderRadius: '8px',
            minWidth: '300px'
          }}
        >
          <Typography variant="mediumParagraph" style={{ color: 'white', fontWeight: 500 }}>
            {notification.message}
          </Typography>
        </Alert>
      </Snackbar>
    </StandardContainer>
  );
};
